import React, { useState, useEffect } from "react";
import SearchBar from "../components/SearchBar";
import ContactContextMenu from "../components/ContactContextMenu";
import WalletFundingBanner from "../components/WalletFundingBanner";
import PendingHandshakesBanner from "../components/PendingHandshakesBanner";
import { Keypair } from '@solana/web3.js';
import { startUserSession } from './../services/AuthService';
import { usePasswordAndUserId } from "../context/AuthContext";
import { walletHasSufficientFunds } from '../utils/Wallet'
import { loadLocalKeys } from "../db/KeyStorageManager";
import { getConversations } from "../db/ConversationMetadata.js";
import { getAllHandshakesForUser } from "../db/HandshakeRequests"
import { formatDistanceToNow } from 'date-fns';
import useConversationStore from "../stores/ConversationStore";
import { uint8ArraysEqual } from "../utils/CryptoHelpers";
import { getEnrichedParticipants, removeOwnViewKey } from "../services/Participants";
import { MESSAGES_TYPES } from "../Config";
import { addContact, deleteContact, getContact, updateContact } from "../db/ContactStorage.js";

import bs58 from 'bs58';

export default function ConversationListView({
  contacts: initialContacts,
  onFundWallet,
  navigate,
  setContacts,
}) {
  const [localContacts, setLocalContacts] = useState([]);
  const [contextContact, setContextContact] = useState(null);
  const [longPressTimer, setLongPressTimer] = useState(null);
  const [search, setSearch] = useState("");
  const [wasLongPress, setWasLongPress] = useState(false);
  const [hasLowFunds, setHasLowFunds] = useState(false);
  const [sessionStarted, setSessionStarted] = useState(false);
  const [localConversations, setLocalConversations] = useState([]);
  const [localKeys, setLocalKeys] = useState(false);
  const { password, userId, salt } = usePasswordAndUserId(); 

  useEffect(() => {
    (async () => {
      const keys = await loadLocalKeys({ password, userId, salt });
      setLocalKeys(keys);
    })();
  }, [password, userId, salt]);

  // set contacts
  useEffect(() => {
    setLocalContacts(initialContacts);
  }, [initialContacts]);

  // load conversations
  useEffect(() => {
    (async () => {
      if (!localKeys) return;

      const conversations = await getConversations(userId, password);

      const enriched = await Promise.all(
        conversations.map(async (conv) => {
          const participants = removeOwnViewKey(conv.participants, localKeys.viewPublicKey);

          const enrichedParticipants = await getEnrichedParticipants({
            participantKeys: participants,
            userId,
            password,
          });

          return {
            ...conv,
            participants: enrichedParticipants,
          };
        })
      );
      
      setLocalConversations(enriched);
    })();
  }, [initialContacts, localKeys]);

  // keep chat meta uptodate
  const latestMessage = useConversationStore(state => state.latestMessage);
  useEffect(() => {
    if (
      latestMessage &&
      latestMessage.conversationId &&
      userId && password &&
      localConversations.length > 0
    ) {
      setLocalConversations(prevConversations => {
        const updated = prevConversations.map(conv => {
          if (uint8ArraysEqual(conv.conversationId, latestMessage.conversationId)) {
            return {
              ...conv,
              lastMessage: {
                message: latestMessage.message,
                timestamp: Date.now(),
                lastSeen: formatDistanceToNow(Date.now(), { addSuffix: true }),
              },
            };
          }
          return conv;
        });

        return updated;
      });
    }
  }, [latestMessage]);

  // start user session
  useEffect(() => {
    let cancelled = false;

    if (!sessionStarted && password && userId && salt) {
      startUserSession({ password, userId, salt })
        .then(() => {
          if (!cancelled) setSessionStarted(true);
        })
        .catch(err => {
          if (!cancelled) {
            console.error("Failed to start user session:", err);
          }
        });
    }

    return () => {
      cancelled = true;
    };
  }, [password, userId, salt, sessionStarted]);

  // check funding wallet balance
  useEffect(() => {
    async function checkBalance() {
      try {
        const keys = await loadLocalKeys({userId, password, salt});
        const pubKey = bs58.encode(keys.fundingPublicKey);
        const ok = await walletHasSufficientFunds(pubKey);
        setHasLowFunds(!ok);
      } catch (err) {
        console.error("Could not check wallet balance", err);
      }
    }

    checkBalance();
  }, []);

  // search
  const filteredConversations = localConversations.filter((conv) =>
    conv.participants.some((p) =>
      p.name.toLowerCase().includes(search.toLowerCase())
    )
  );

  const handleLongPressStart = (conversation) => {
    const timer = setTimeout(() => {

      if (conversation.participants.length === 1) {
        setContextContact(conversation.participants[0]);
      }
      setWasLongPress(true);
    }, 600);
    setLongPressTimer(timer);
  };

  const handleLongPressEnd = () => {
    clearTimeout(longPressTimer);
    setTimeout(() => setWasLongPress(false), 100);
  };

  const handleDelete = async ({
    identityViewPublicKey,
    name,
  }) => {
    setContextContact(null);
    const updatedContacts = await deleteContact({
      identityViewPublicKey,
      password,
      userId,
    });
    setContacts(updatedContacts);
    setContextContact(null);
  };

  const handleRename = async ({
    identityViewPublicKey,
    name,
  }) => {

    let updatedContacts;
    if (await getContact({
      identityViewPublicKey,
      userId,
      password,
    })) {
      updatedContacts = await updateContact({
        identityViewPublicKey,
        name,
        userId,
        password,
      });
    }
    else {

      updatedContacts = await addContact({
        identityViewPublicKey,
        name,
        userId, 
        password,
      });
    }

    setContacts(updatedContacts);
    setContextContact(null);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", backgroundColor: "#000", color: "#fff" }}>
      {/* Sticky Logo Header */}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          backgroundColor: "#000",
          borderBottom: "1px solid #222",
          padding: "1rem",
          display: "flex",
          justifyContent: "left",
        }}
      >
        <h2 style={{ margin: 0 }}>
          Unlink
          <img
            src="/images/logo.png"
            alt="Unlink Logo"
            style={{
              width: "60px",
              height: "auto",
              opacity: 0.95,
              marginLeft: "10px",
              position: "relative",
              top: "2px",
            }}
          />
        </h2>
      </div>

      {/* Scrollable content */}
      <div style={{ flex: 1, overflowY: "auto", padding: "1rem 2rem", boxSizing: "border-box" }}>
        {contextContact && (
          <ContactContextMenu
            contact={contextContact}
            onClose={() => setContextContact(null)}
            onDelete={handleDelete}
            onRename={handleRename}
          />
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          <SearchBar value={search} onChange={setSearch} />

          {hasLowFunds && <WalletFundingBanner onFund={onFundWallet} />}

          {filteredConversations.map((conversation) => (
            <button
              key={conversation.conversationId}
              onClick={() => {
                if (wasLongPress) return;

                navigate({
                  name: 'chat',
                  props: {
                    conversationId: conversation.conversationId,
                  },
                });
              }}
              onMouseDown={() => handleLongPressStart(conversation)}
              onTouchStart={() => handleLongPressStart(conversation)}
              onMouseUp={handleLongPressEnd}
              onTouchEnd={handleLongPressEnd}
              onMouseLeave={handleLongPressEnd}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "0.75rem",
                padding: "0 0.25rem",
                backgroundColor: "transparent",
                border: "none",
                borderRadius: "0",
                textAlign: "left",
                cursor: "pointer",
                fontSize: "1rem",
                width: "100%",
              }}
            >
              <img
                src="/images/avatar.png"
                alt="avatar"
                width="48"
                height="48"
                style={{ borderRadius: "24px", objectFit: "cover", flexShrink: 0 }}
              />

              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                 <span style={{ fontWeight: "bold", color: "#fff", textTransform: "capitalize", marginTop: "7px" }}>
                    {conversation.participants.map(p => p.name).join(', ')}
                  </span>
                 <span
                  style={{
                    fontSize: "0.8rem",
                    color: conversation.lastMessage?.read === false ? "#4e8cff" : "rgb(125, 125, 125)",
                    marginTop: "5px",
                    fontWeight: conversation.lastMessage?.read === false ? "bold" : "normal",
                  }}
                >
                  {new Date(conversation.lastMessage?.timestamp).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
                </div>
                <div
                  style={{
                    fontSize: "0.85rem",
                    color: "#ccc",
                    marginTop: "0.25rem",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                  }}
                >
                  <img
                    src="/images/checkmark.png"
                    alt="Read"
                    width="16"
                    height="16"
                    style={{ filter: "invert(60%)", opacity: 0.8 }}
                  />
                  
                  <span>
                    {(() => {
                      const last = conversation?.lastMessage?.message;
                      if (!last) return "No messages yet";

                      const { message, messageType, senderViewPublicKey } = last;
                      const isMe = uint8ArraysEqual(localKeys.viewPublicKey, conversation.senderViewPublicKey);

                      if (messageType === MESSAGES_TYPES.HANDSHAKE_REQUEST) {
                        return isMe ? "You sent a handshake request" : "Handshake request received";
                      }

                      return isMe ? `you: ${message}` : message;
                    })()}
                  </span>

                  {conversation.lastMessage?.read === false && (
                    <div
                      style={{
                        minWidth: "18px",
                        height: "18px",
                        borderRadius: "9px",
                        backgroundColor: "#4e8cff",
                        color: "#000",
                        fontSize: "0.75rem",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: "bold",
                        marginLeft: "auto",
                      }}
                    >
                      1
                    </div>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}